import { useEffect, useMemo, useState } from "react";
import { DatePicker, Select, Table } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { CalendarOutlined, BankOutlined } from "@ant-design/icons";
import PdfExcelPrint from "../Utilities/PdfExcelPrint";
import styles from "../shared/Inventory.module.css";
import InventoryPageHeader from "../shared/InventoryPageHeader";
import InventoryTable from "../shared/InventoryTable";
import { CREATE_jwel } from "../Config/Config";
import Header from "../Header";
import SidebarDrawer from "../SidebarDrawer";

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ─── filter box theme palette (same as CashBook / OutstandingCustomers) ─── */
const FILTER_THEMES = {
  blue: {
    text: "#1d4ed8",
    bg: "#ffffff",
    border: "#93c5fd",
    ring: "rgba(29,78,216,0.15)",
  },
  purple: {
    text: "#7c3aed",
    bg: "#ffffff",
    border: "#c4b5fd",
    ring: "rgba(124,58,237,0.15)",
  },
};

const FilterField = ({ icon, label, theme = "blue", width, children }) => {
  const c = FILTER_THEMES[theme];
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: c.bg,
        border: `2px solid ${c.border}`,
        borderRadius: 12,
        padding: "7px 10px 9px",
        minHeight: 64,
        width,
        boxShadow: `0 1px 3px ${c.ring}`,
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: c.text,
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {icon} {label}
      </span>
      <div
        style={{
          background: "#fff",
          border: "1.5px solid #d6dbe3",
          borderRadius: 8,
          padding: "1px 8px",
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ─── small colored amount chips (Credit/Debit), same visual family as
   the CashBook Receipt/Payment chips ─── */
const amountChipStyle = (kind) => {
  const palette = {
    credit: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
    debit: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  }[kind];

  return {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 12.5,
    fontWeight: 700,
    background: palette.bg,
    color: palette.color,
    border: `1px solid ${palette.border}`,
  };
};

/* ---------------- Shared card-view field maps (mobile) ---------------- */
const primaryFields = [
  { key: "PARTICULARS", label: "Particulars" },
  { key: "DESCRIPTION", label: "Description" },
];
const secondaryFields = [
  { key: "JAMAAMOUNT", label: "Credit" },
  { key: "NAMAAMOUNT", label: "Debit" },
  { key: "BALANCE", label: "Balance" },
];

const BankStatement = () => {
  const [data, setData] = useState([]);
  const [openingCash, setOpeningCash] = useState(0);
  const [totalAccAmount, setTotalAccAmount] = useState(0);
  const [dates, setDates] = useState([dayjs(), dayjs()]);
  const [accountDropDown, setAccountDropDown] = useState([]);
  const [selectAccount, setSelectAccount] = useState();
  const [loading, setLoading] = useState(false);
  const [tableVisible, setTableVisible] = useState(false);
  const tenantName = localStorage.getItem("tenantName");
  const [open, setOpen] = useState(false);
  const userArea = localStorage.getItem("city");
  const userName = localStorage.getItem("user");
  const singleImage = localStorage.getItem("singleImage");

  const toggleDrawer = () => {
    setOpen(false);
  };

  /* ---- data / API logic (unchanged endpoints, same query params/formats) ---- */

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetBankStatementOpening?fromDate=${dayjs(
          dates[0],
        ).format("MM/DD/YYYY")}&ledgerName=${selectAccount}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const openingData = response.data;

        if (Array.isArray(openingData) && openingData.length > 0) {
          setOpeningCash(openingData[0].NAMAAMOUNT - openingData[0].JAMAAMOUNT);
        }
      })
      .catch((error) => {
        console.error("Error fetching opening cash:", error);
      });
  }, [dates, selectAccount]);

  const accountDropDownAPI = async () => {
    try {
      const response = await axios.get(
        `${CREATE_jwel}/api/ERP/GetBankStatementForAccNo`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      );

      const accData = response.data;

      if (Array.isArray(accData) && accData.length > 0) {
        setAccountDropDown(accData);
      }

      const total = accData.reduce((acc, item) => {
        const jama = Number(item.JAMAAMOUNT) || 0;
        const nama = Number(item.NAMAAMOUNT) || 0;
        return acc + (nama - jama);
      }, 0);

      setTotalAccAmount(total);
    } catch (error) {
      console.error("Error fetching estimation count:", error);
    }
  };

  useEffect(() => {
    if (!dates[0] || !dates[1]) return;

    setLoading(true);
    setTableVisible(false);

    const fromDate = dayjs(dates[0]).format("YYYY-MM-DD");
    const toDate = dayjs(dates[1]).format("YYYY-MM-DD");

    axios
      .get(
        `${CREATE_jwel}/api/ERP/GetBankStatementData?fromDate=${fromDate}&toDate=${toDate}&ledgerName=${selectAccount}`,
        {
          headers: {
            tenantName: tenantName,
          },
        },
      )
      .then((response) => {
        const detailsData = response.data;
        let balance = openingCash;

        const calculatedData = detailsData.map((item, index) => {
          balance = balance + item.NAMAAMOUNT - item.JAMAAMOUNT;
          return {
            ...item,
            BALANCE: balance,
            key: index + 1,
            serialNo: index + 1,
          };
        });

        setData(calculatedData);
        setTimeout(() => setTableVisible(true), 80);
      })
      .catch((error) => {
        console.error("Error fetching cash book details:", error);
      })
      .finally(() => setLoading(false));
  }, [dates, openingCash]);

  useEffect(() => {
    accountDropDownAPI();
  }, []);

  useEffect(() => {
    if (!selectAccount && accountDropDown.length > 0) {
      setSelectAccount(accountDropDown[0].AccNo);
    }
  }, [accountDropDown, selectAccount]);

  const columns = [
    {
      title: "S.No",
      dataIndex: "serialNo",
      width: 50,
      align: "center",
      key: "serialNo",
      className: "col-sno",
    },
    {
      title: "Entry Date",
      dataIndex: "ENTRYDATE",
      key: "ENTRYDATE",
      align: "center",
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Particulars",
      dataIndex: "PARTICULARS",
      key: "PARTICULARS",
      render: (value) => <span className="tagBadge">{value}</span>,
    },
    { title: "Description", dataIndex: "DESCRIPTION", key: "DESCRIPTION" },
    {
      title: "Credit",
      dataIndex: "JAMAAMOUNT",
      key: "JAMAAMOUNT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("credit")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Debit",
      dataIndex: "NAMAAMOUNT",
      key: "NAMAAMOUNT",
      align: "right",
      render: (value) => (
        <span style={amountChipStyle("debit")}>
          {Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "BALANCE",
      key: "BALANCE",
      align: "right",
      render: (value) => (
        <strong style={{ color: Number(value) < 0 ? "#ef4444" : "#0f766e" }}>
          {Number(value || 0).toFixed(2)}
        </strong>
      ),
    },
  ];

  const totals = useMemo(() => {
    return data.reduce(
      (acc, item) => {
        acc.debit += Number(item.NAMAAMOUNT || 0);
        acc.credit += Number(item.JAMAAMOUNT || 0);
        return acc;
      },
      { debit: 0, credit: 0 },
    );
  }, [data]);

  const closingBalance =
    data.length > 0 ? data[data.length - 1].BALANCE : openingCash;

  const closingCash = openingCash - totalAccAmount;

  const formattedData = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        serialNo: index + 1,
        NAMAAMOUNT: Number(item.NAMAAMOUNT || 0),
        JAMAAMOUNT: Number(item.JAMAAMOUNT || 0),
        BALANCE: Number(item.BALANCE || 0),
        ENTRYDATE: item?.ENTRYDATE
          ? dayjs(item.ENTRYDATE).format("DD/MMM/YYYY")
          : "",
        VDATE: item?.VDATE ? dayjs(item.VDATE).format("DD/MMM/YYYY") : "",
      })),
    [data],
  );

  const exportData = [
    ...formattedData,
    {
      serialNo: "Total",
      ENTRYDATE: "",
      PARTICULARS: "",
      DESCRIPTION: "",
      NAMAAMOUNT: totals.debit.toFixed(2),
      JAMAAMOUNT: totals.credit.toFixed(2),
      BALANCE: closingBalance.toFixed(2),
    },
  ];

  return (
    <div className={styles.page}>
      <Header setOpen={setOpen} />
      <style>{`
        .bs-acc-dropdown .ant-select-item {
          font-size: 13.5px !important;
          font-weight: 600 !important;
          padding: 8px 12px !important;
        }
        .bs-acc-dropdown .ant-select-item-option-selected {
          background-color: #f5f3ff !important;
        }
        .bs-acc-dropdown .ant-select-item-option-active {
          background-color: #f0f9ff !important;
        }
        .ant-table-thead > tr > th {
          white-space: nowrap !important;
        }
      `}</style>

      <InventoryPageHeader
        title="Bank Statement"
        subtitle="Accounts · Bank Statement"
        icon={<BankOutlined style={{ color: "#fff", fontSize: 16 }} />}
        recordCount={data.length}
        visible={data.length > 0}
        stats={[
          { label: "Records", value: data.length, color: "blue" },
          {
            label: "Opening Cash",
            value: openingCash.toFixed(2),
            color: "amber",
          },
          { label: "Credit", value: totals.credit.toFixed(2), color: "green" },
          { label: "Debit", value: totals.debit.toFixed(2), color: "red" },
          {
            label: "Closing Cash",
            value: closingCash.toFixed(2),
            color: "teal",
          },
        ]}
      />

      <div
        className={styles.toolbar}
        style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
      >
        <div
          className={styles.toolbarLeft}
          style={{ flexWrap: "wrap", gap: 10, alignItems: "stretch" }}
        >
          <FilterField
            icon={<CalendarOutlined />}
            label="Date Range"
            theme="blue"
          >
            <RangePicker
              value={dates}
              onChange={(range) => {
                if (range?.[0] && range?.[1]) setDates(range);
              }}
              format="DD/MM/YYYY"
              size="middle"
              bordered={false}
              allowClear={false}
              style={{ width: 240 }}
              disabledDate={(current) =>
                current && current.isAfter(dayjs(), "day")
              }
            />
          </FilterField>

          <FilterField icon={<BankOutlined />} label="Account" theme="purple">
            <Select
              showSearch
              allowClear={false}
              placeholder="Select account"
              size="middle"
              bordered={false}
              popupClassName="bs-acc-dropdown"
              style={{ width: 160 }}
              value={selectAccount || undefined}
              onChange={(value) => setSelectAccount(value)}
              filterOption={(input, option) =>
                (option?.children || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {accountDropDown.map((acc, index) => (
                <Option key={index} value={acc.AccNo}>
                  {acc.AccNo}
                </Option>
              ))}
            </Select>
          </FilterField>
        </div>

        <div className={styles.toolbarRight}>
          <PdfExcelPrint
            data={exportData}
            columns={columns}
            fileName="BankStatement"
            totals={{
              totalDebit: totals.debit.toFixed(2),
              totalCredit: totals.credit.toFixed(2),
            }}
            disabled={formattedData.length === 0}
          />
        </div>
      </div>

      <InventoryTable
        columns={columns}
        dataSource={formattedData}
        rowKey="serialNo"
        tableClassName="mr-table"
        tableEnter={tableVisible}
        loading={loading}
        emptyText="No bank statement entries found for the selected filters"
        primaryFields={primaryFields}
        secondaryFields={secondaryFields}
        summary={() => (
          <Table.Summary.Row style={{ background: "#f5f5f5", fontWeight: 600 }}>
            <Table.Summary.Cell>Total</Table.Summary.Cell>
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell />
            <Table.Summary.Cell align="right">
              {totals.credit.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {totals.debit.toFixed(2)}
            </Table.Summary.Cell>
            <Table.Summary.Cell align="right">
              {closingBalance.toFixed(2)}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      <SidebarDrawer
        open={open}
        toggleDrawer={toggleDrawer}
        singleImage={singleImage}
        userArea={userArea}
        userName={userName}
      />
    </div>
  );
};

export default BankStatement;
